<template>
  <div class="text-layer-control space-y-3 border-b border-t p-4 mb-4 bg-white shadow-sm">
    <div class="flex justify-between items-center">
      <span class="text-xs text-gray-400">Label text #{{ localLayer.id.slice(0, 8) }}</span>
      <IconDelete class="cursor-pointer" @click="$emit('delete', localLayer.id)" />
    </div>

    <!-- CONTROLS -->
    <div class="text-layer-controls flex flex-wrap gap-x-4 gap-y-2">
      <!-- Font family -->
      <div class="inline-flex items-center gap-1">
        <label>Font</label>
        <select v-model="localLayer.font" class="select" @change="onFontChange">
          <option v-for="font in Object.keys(fontOptions)" :key="font" :value="font">
            {{ font }}
          </option>
        </select>
      </div>

      <!-- Font weight -->
      <div class="inline-flex items-center gap-1">
        <label>Weight</label>
        <select v-model="localLayer.fontWeight" class="select">
          <option v-for="weight in availableFontWeights" :key="weight" :value="weight">
            {{ weight }}
          </option>
        </select>
      </div>

      <!-- Font size -->
      <div class="inline-flex items-center gap-1">
        <label>Size</label>
        <select v-model.number="localLayer.fontSize" class="select w-16">
          <option v-for="size in fontSizes" :key="size" :value="size">{{ size }} pt</option>
        </select>
      </div>

      <!-- Color -->
      <div class="inline-flex items-center gap-1">
        <label>Color:</label>
        <input
          type="color"
          style="background: none"
          v-model="localLayer.color"
          class="h-6 w-6 border-none outline-0 bg-white"
        />
      </div>

      <!-- Align -->
      <div class="inline-flex alignment-group">
        <button
          v-for="option in alignOptions"
          :key="option.value"
          :title="option.value"
          class="align-btn"
          :class="{ active: localLayer.align === option.value }"
          @click="localLayer.align = option.value as 'left' | 'center' | 'right' | 'justify'"
        >
          <span v-html="option.icon" />
        </button>
      </div>
    </div>

    <!-- Text -->
    <div>
      <UiInput v-model="localLayer.text" placeholder="Zadajte text" textarea />
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, watch, onMounted, toRaw, ref } from 'vue'
import WebFont from 'webfontloader'
import IconDelete from '@/components/ui/IconDelete.vue'
import UiInput from '@/components/ui/UiInput.vue'
import type { TextLayer } from '@/stores/designerStore'

const props = defineProps<{
  layer: TextLayer
}>()

const emit = defineEmits<{
  (e: 'update:layer', updated: TextLayer): void
  (e: 'delete', id: string): void
}>()

const localLayer = reactive({ ...props.layer })

watch(
  () => props.layer,
  (newLayer) => {
    Object.assign(localLayer, newLayer)
  },
  { deep: true },
)

watch(
  localLayer,
  () => {
    emit('update:layer', toRaw(localLayer))
  },
  { deep: true },
)

const fontOptions: Record<string, string[]> = {
  Arial: ['400', '700'],
  Roboto: ['100', '300', '400', '500', '700', '900'],
  'Open Sans': ['300', '400', '600', '700', '800'],
}

const systemFonts = ['Arial']
const availableFontWeights = ref<string[]>([])

const fontSizes = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64, 72]

const loadGoogleFont = (font: string) => {
  if (systemFonts.includes(font)) return
  const weights = fontOptions[font]?.join(',') || '400'
  WebFont.load({
    google: {
      families: [`${font}:${weights}`],
    },
  })
}

const fixFontWeight = () => {
  const weights = fontOptions[localLayer.font]
  if (!weights) return
  if (!weights.includes(localLayer.fontWeight)) {
    localLayer.fontWeight = weights.includes('400') ? '400' : weights[0]
  }
}

const onFontChange = () => {
  availableFontWeights.value = fontOptions[localLayer.font] || ['400']
  loadGoogleFont(localLayer.font)
  fixFontWeight()
}

onMounted(() => {
  onFontChange()
})

const alignOptions = [
  {
    value: 'left',
    icon: `<svg width="20" height="20" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5 5.5H2M10.5 3.5H2M10.5 7.5H2M8.5 9.5H2" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
  },
  {
    value: 'center',
    icon: `<svg width="20" height="20" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.5 5.5H3.5M11 3.5H2M11 7.5H2M9.5 9.5H3.5" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
  },
  {
    value: 'right',
    icon: `<svg width="20" height="20" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 5.5H4.5M11 3.5H2.5M11 7.5H2.5M11 9.5H4.5" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
  },
  {
    value: 'justify',
    icon: `<svg width="20" height="20" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 5.5H2M11 9.5H2M11 3.5H2M11 7.5H2" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
  },
]
</script>

<style scoped>
.input {
  @apply border px-2 py-1 rounded;
}
.select {
  @apply border px-2 py-1 rounded;
}

.alignment-group {
  display: inline-flex;
  background: #fff;
  border: 0.5px solid #e5e5e5;
  box-shadow:
    0px 1px 3px rgba(16, 24, 40, 0.1),
    0px 1px 2px rgba(16, 24, 40, 0.06);
  border-radius: 4px;
  overflow: hidden;
}

.align-btn {
  all: unset;
  padding: 4px 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 0.5px solid #e5e5e5;

  &:first-child {
    border-left: none;
  }

  svg {
    display: block;
  }

  &.active {
    background-color: #f5f5f5;
  }

  &:hover {
    background-color: #f9f9f9;
  }
}

.text-layer-controls {
  select {
    border: 1px solid #e5e5e5;
    padding: 4px;
    border-radius: 4px;
  }
}
</style>
